import {
  LinearFilter,
  LinearMipmapLinearFilter,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from 'three';

export const MAX_CACHE_SIZE = 300;
/**
 * ALKIS cache budget. HiDPI tiles are 1024px+ (4 MB RGBA each), so the overlay
 * cache is capped well below the basemap cache: ~10 visible tiles plus panning
 * headroom, bounding worst-case GPU memory around 400 MB.
 */
export const ALKIS_MAX_CACHE_SIZE = 96;
/** Default ALKIS anisotropy; override via setMaxAnisotropy with renderer caps. */
export const ALKIS_DEFAULT_ANISOTROPY = 8;
const loader = new TextureLoader();

let maxAnisotropy = ALKIS_DEFAULT_ANISOTROPY;

/** Lets app init clamp ALKIS anisotropy to renderer capabilities. */
export const setMaxAnisotropy = (value: number): void => {
  if (Number.isFinite(value) && value > 0) {
    maxAnisotropy = Math.min(Math.floor(value), 16);
  }
};

// JavaScript Map maintains key insertion order.
// When an entry is accessed or added, deleting and re-inserting it moves it to the "newest" position (MRU).
// The first key returned by cache.keys().next().value is always the oldest (LRU), giving strict O(1) eviction.
const alkisCache = new Map<string, Texture>();
const basemapCache = new Map<string, Texture>();
const pendingAlkisLoads = new Map<string, Promise<Texture>>();
const pendingBasemapLoads = new Map<string, Promise<Texture>>();

/**
 * Evicts the oldest entries in O(1) time per eviction using Map's insertion order.
 */
const evictOldest = (cache: Map<string, Texture>, limit: number): void => {
  while (cache.size > limit) {
    const oldestKey = cache.keys().next().value;
    if (!oldestKey) break;
    const texture = cache.get(oldestKey);
    texture?.dispose();
    cache.delete(oldestKey);
  }
};

/**
 * Updates MRU position in O(1) and returns cached texture if present.
 */
const touchAndGet = (cache: Map<string, Texture>, url: string): Texture | undefined => {
  const texture = cache.get(url);
  if (texture) {
    cache.delete(url);
    cache.set(url, texture);
    return texture;
  }
  return undefined;
};

export const getCachedAlkisTexture = (url: string): Texture | undefined => {
  return touchAndGet(alkisCache, url);
};

export const loadAlkisTexture = async (url: string): Promise<Texture> => {
  const cached = getCachedAlkisTexture(url);
  if (cached) return cached;

  const pending = pendingAlkisLoads.get(url);
  if (pending) return pending;

  const promise = loader
    .loadAsync(url)
    .then((texture) => {
      pendingAlkisLoads.delete(url);
      texture.premultiplyAlpha = false;
      texture.generateMipmaps = true;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.magFilter = LinearFilter;
      texture.anisotropy = maxAnisotropy;
      texture.colorSpace = SRGBColorSpace;
      texture.needsUpdate = true;

      alkisCache.delete(url);
      alkisCache.set(url, texture);
      evictOldest(alkisCache, ALKIS_MAX_CACHE_SIZE);
      return texture;
    })
    .catch((err) => {
      pendingAlkisLoads.delete(url);
      throw err;
    });

  pendingAlkisLoads.set(url, promise);
  return promise;
};

export const getCachedBasemapTexture = (url: string): Texture | undefined => {
  return touchAndGet(basemapCache, url);
};

export const loadBasemapTexture = async (url: string): Promise<Texture> => {
  const cached = getCachedBasemapTexture(url);
  if (cached) return cached;

  const pending = pendingBasemapLoads.get(url);
  if (pending) return pending;

  const promise = loader
    .loadAsync(url)
    .then((texture) => {
      pendingBasemapLoads.delete(url);
      texture.premultiplyAlpha = false;
      texture.needsUpdate = true;

      basemapCache.delete(url);
      basemapCache.set(url, texture);
      evictOldest(basemapCache, MAX_CACHE_SIZE);
      return texture;
    })
    .catch((err) => {
      pendingBasemapLoads.delete(url);
      throw err;
    });

  pendingBasemapLoads.set(url, promise);
  return promise;
};

export const clearTileTextureCache = (): void => {
  for (const texture of alkisCache.values()) {
    texture.dispose();
  }
  alkisCache.clear();
  pendingAlkisLoads.clear();

  for (const texture of basemapCache.values()) {
    texture.dispose();
  }
  basemapCache.clear();
  pendingBasemapLoads.clear();
};
